import { useState, type ReactNode } from 'react';
import { Dialog as D } from 'radix-ui';
import { AnimatePresence, m } from 'motion/react';
import { X } from 'lucide-react';
import { cx } from '@/lib/cx';
import { useIsDesktop } from '@/lib/useMediaQuery';
import { ease } from '../motion/presets';
import { Button } from './Button';
import { Banner } from './Display';
import { Field, TextArea } from './Field';

interface BaseProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Prevents closing by clicking outside (forms with data). */
  modal?: boolean;
}

const widths = { sm: 'max-w-md', md: 'max-w-lg', lg: 'max-w-2xl', xl: 'max-w-4xl' };

export function Dialog({ open, onOpenChange, title, description, children, footer, size = 'md' }: BaseProps) {
  return (
    <D.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <D.Portal forceMount>
            <D.Overlay asChild forceMount>
              <m.div
                className="fixed inset-0 z-50 bg-ink-900/25 backdrop-blur-[2px]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              />
            </D.Overlay>
            <D.Content asChild forceMount>
              <m.div
                className={cx(
                  'fixed inset-x-3 bottom-3 z-50 mx-auto flex max-h-[88dvh] flex-col overflow-hidden sm:inset-x-6 sm:top-[10vh] sm:bottom-auto',
                  'rounded-(--radius-sheet) border border-line bg-surface-0 shadow-e4',
                  widths[size],
                )}
                initial={{ opacity: 0, y: 18, scale: 0.97, rotateX: 6 }}
                animate={{ opacity: 1, y: 0, scale: 1, rotateX: 0 }}
                exit={{ opacity: 0, y: 10, scale: 0.98, transition: { duration: 0.14, ease: ease.exit } }}
                transition={{ duration: 0.32, ease: ease.emphasized }}
                style={{ transformPerspective: 1200 }}
              >
                <div className="gold-hairline absolute inset-x-8 top-0" />
                <header className="flex items-start gap-4 px-6 pt-5 pb-3">
                  <div className="min-w-0 flex-1">
                    <D.Title className="font-display text-[22px] leading-tight text-ink-900">{title}</D.Title>
                    {description ? (
                      <D.Description className="mt-1 text-[13.5px] text-ink-500">{description}</D.Description>
                    ) : (
                      <D.Description className="sr-only">{typeof title === 'string' ? title : 'Diálogo'}</D.Description>
                    )}
                  </div>
                  <D.Close
                    className="-mr-2 rounded-lg p-2 text-ink-400 transition hover:bg-surface-2 hover:text-ink-900"
                    aria-label="Cerrar"
                  >
                    <X className="size-4" />
                  </D.Close>
                </header>
                <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto px-6 pb-5">{children}</div>
                {footer && (
                  <footer className="flex flex-wrap items-center justify-end gap-2 border-t border-line bg-surface-1/70 px-6 py-3.5">
                    {footer}
                  </footer>
                )}
              </m.div>
            </D.Content>
          </D.Portal>
        )}
      </AnimatePresence>
    </D.Root>
  );
}

/** Side panel on desktop, bottom sheet on phones: for quick creation without leaving the screen. */
export function Sheet({ open, onOpenChange, title, description, children, footer }: BaseProps) {
  const desktop = useIsDesktop();
  return (
    <D.Root open={open} onOpenChange={onOpenChange}>
      <AnimatePresence>
        {open && (
          <D.Portal forceMount>
            <D.Overlay asChild forceMount>
              <m.div
                className="fixed inset-0 z-50 bg-ink-900/20 backdrop-blur-[1.5px]"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              />
            </D.Overlay>
            <D.Content asChild forceMount>
              <m.aside
                className={cx(
                  'fixed z-50 flex flex-col overflow-hidden border-line bg-surface-0 shadow-e4',
                  'inset-x-0 bottom-0 max-h-[92dvh] rounded-t-(--radius-sheet) border-t',
                  'sm:inset-y-3 sm:right-3 sm:left-auto sm:max-h-none sm:w-[440px] sm:rounded-(--radius-sheet) sm:border',
                )}
                initial={desktop ? { x: 48, opacity: 0 } : { y: '100%' }}
                animate={desktop ? { x: 0, opacity: 1 } : { y: 0 }}
                exit={
                  desktop
                    ? { x: 36, opacity: 0, transition: { duration: 0.18, ease: ease.exit } }
                    : { y: '100%', transition: { duration: 0.2, ease: ease.exit } }
                }
                transition={{ type: 'spring', stiffness: 340, damping: 34 }}
              >
                <div className="mx-auto mt-2 h-1 w-10 rounded-full bg-line-strong sm:hidden" />
                <header className="flex items-start gap-3 border-b border-line px-6 pt-5 pb-4">
                  <div className="min-w-0 flex-1">
                    <D.Title className="font-display text-[22px] leading-tight">{title}</D.Title>
                    <D.Description className={description ? 'mt-1 text-[13.5px] text-ink-500' : 'sr-only'}>
                      {description ?? (typeof title === 'string' ? title : 'Panel')}
                    </D.Description>
                  </div>
                  <D.Close className="-mr-2 rounded-lg p-2 text-ink-400 hover:bg-surface-2 hover:text-ink-900" aria-label="Cerrar">
                    <X className="size-4" />
                  </D.Close>
                </header>
                <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto px-6 py-5">{children}</div>
                {footer && <footer className="flex items-center justify-end gap-2 border-t border-line bg-surface-1/70 px-6 py-3.5">{footer}</footer>}
              </m.aside>
            </D.Content>
          </D.Portal>
        )}
      </AnimatePresence>
    </D.Root>
  );
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirmar',
  tone = 'gold',
  loading,
  onConfirm,
  children,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  confirmLabel?: string;
  tone?: 'gold' | 'danger' | 'primary';
  loading?: boolean;
  onConfirm: () => void;
  children?: ReactNode;
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Volver
          </Button>
          <Button variant={tone} loading={loading} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {children}
    </Dialog>
  );
}

/** Actions that require a reason (≤ 240 characters), with quick suggestions. */
export function ReasonDialog({
  open,
  onOpenChange,
  title,
  description,
  label = 'Motivo',
  confirmLabel = 'Confirmar',
  tone = 'gold',
  suggestions = [],
  loading,
  onConfirm,
  max = 240,
  error,
  children,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  /** Server error to show inside the dialog. */
  error?: ReactNode;
  /** Extra context shown above the reason (a summary of what is about to happen). */
  children?: ReactNode;
  label?: string;
  confirmLabel?: string;
  tone?: 'gold' | 'danger' | 'primary';
  suggestions?: string[];
  loading?: boolean;
  onConfirm: (reason: string) => void;
  max?: number;
}) {
  const [reason, setReason] = useState('');
  const valid = reason.trim().length > 0 && reason.length <= max;
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) setReason('');
        onOpenChange(o);
      }}
      title={title}
      description={description}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Volver
          </Button>
          <Button variant={tone} loading={loading} disabled={!valid} onClick={() => onConfirm(reason.trim())}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {error && (
        <Banner tone="danger" className="mb-3">
          {error}
        </Banner>
      )}
      {children && <div className="mb-3">{children}</div>}
      {suggestions.length > 0 && (
        <div className="mb-3 flex flex-wrap gap-1.5">
          {suggestions.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setReason(s)}
              className={cx(
                'rounded-full border px-3 py-1 text-[12.5px] transition',
                reason === s
                  ? 'border-gold-400 bg-gold-50 text-gold-700'
                  : 'border-line-strong text-ink-700 hover:border-gold-300 hover:bg-gold-50/50',
              )}
            >
              {s}
            </button>
          ))}
        </div>
      )}
      <Field label={label} aside={<span className="tabular text-[11.5px] text-ink-400">{reason.length}/{max}</span>}>
        {(p) => (
          <TextArea {...p} value={reason} maxLength={max} autoFocus onChange={(e) => setReason(e.target.value)} />
        )}
      </Field>
    </Dialog>
  );
}
